// Two Way Mapping for one button — pair it with a button on another switch so
// the two mirror each other, like a staircase light.
//
// Shared by both shells. The partner is chosen from the devices at the SAME
// LOCATION, which is the only set that can be wired together in practice.
//
// See core/devices/twoWay for the three encoding traps this relies on.

import React, { useCallback, useEffect, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, MenuItem, TextField,
  List, ListItem, ListItemText, IconButton, Typography, Box, CircularProgress, Alert,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import SyncAltIcon from '@mui/icons-material/SyncAlt';
import { getDevicesByLocation, getDeviceDetail, getOutputs } from '../../core/api/api';
import {
  getMappingsFor, mappingsForOutput, addTwoWayMapping, deleteTwoWayMapping,
} from '../../core/devices/twoWay';

const asArray = (res) => (res && Array.isArray(res.data) ? res.data : []);

export default function TwoWayMappingDialog({
  open, uniqueNumber, output, outputName, onClose,
}) {
  const [pairs, setPairs] = useState(null);
  const [siblings, setSiblings] = useState([]);
  const [partnerUno, setPartnerUno] = useState('');
  const [partnerOutputs, setPartnerOutputs] = useState([]);
  const [partnerOutput, setPartnerOutput] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const loadPairs = useCallback(async () => {
    try {
      const all = await getMappingsFor(uniqueNumber);
      setPairs(mappingsForOutput(all, output));
    } catch (e) {
      setPairs([]);
      setError('Could not load existing mappings.');
    }
  }, [uniqueNumber, output]);

  // Candidate partners: the other devices at this location.
  const loadSiblings = useCallback(async () => {
    const detail = asArray(await getDeviceDetail(uniqueNumber).catch(() => null))[0];
    const locationId = detail && (detail.LocationID ?? detail.LocationId);
    if (locationId == null) { setSiblings([]); return; }
    const rows = asArray(await getDevicesByLocation(locationId).catch(() => null));
    setSiblings(rows.filter((d) => String(d.UniqueNumber) !== String(uniqueNumber)));
  }, [uniqueNumber]);

  useEffect(() => {
    if (!open) return;
    setError('');
    setPairs(null);
    setPartnerUno('');
    setPartnerOutput('');
    setPartnerOutputs([]);
    loadPairs();
    loadSiblings();
  }, [open, loadPairs, loadSiblings]);

  // The chosen partner's buttons, by name rather than number.
  useEffect(() => {
    if (!partnerUno) { setPartnerOutputs([]); return; }
    getOutputs(partnerUno)
      .then((res) => setPartnerOutputs(asArray(res)))
      .catch(() => setPartnerOutputs([]));
    setPartnerOutput('');
  }, [partnerUno]);

  const add = async () => {
    if (!partnerUno || !partnerOutput) return;
    setBusy(true);
    setError('');
    try {
      await addTwoWayMapping({
        firstUniqueNumber: uniqueNumber,
        firstOutput: output,
        secondUniqueNumber: partnerUno,
        secondOutput: Number(partnerOutput),
      });
      setPartnerUno('');
      setPartnerOutput('');
      await loadPairs();
    } catch (e) {
      setError('Could not add the mapping.');
    } finally {
      setBusy(false);
    }
  };

  const remove = async (m) => {
    setBusy(true);
    setError('');
    try {
      await deleteTwoWayMapping(uniqueNumber, output, m.partnerUniqueNumber, m.partnerOutput);
      await loadPairs();
    } catch (e) {
      setError(e.message || 'Could not remove the mapping.');
    } finally {
      setBusy(false);
    }
  };

  const nameOfDevice = (uno) => {
    const d = siblings.find((x) => String(x.UniqueNumber) === String(uno));
    return d ? (d.DeviceName || uno) : uno;
  };

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <SyncAltIcon />
        Two Way Mapping
      </DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          {`Press either button and both change together. Pairing `}
          <b>{outputName || `Button ${output}`}</b>
          {' with a button on another switch.'}
        </Typography>

        {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}

        {pairs === null ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 2 }}>
            <CircularProgress size={18} />
            <Typography variant="body2">Loading…</Typography>
          </Box>
        ) : pairs.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            Not paired with anything yet.
          </Typography>
        ) : (
          <List dense disablePadding>
            {pairs.map((m) => (
              <ListItem
                key={`${m.partnerUniqueNumber}-${m.partnerOutput}`}
                secondaryAction={(
                  <IconButton edge="end" onClick={() => remove(m)} disabled={busy}>
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                )}
              >
                <ListItemText
                  primary={m.partnerOutputName || `Button ${m.partnerOutput}`}
                  secondary={`${m.partnerDeviceName || nameOfDevice(m.partnerUniqueNumber)} · ${m.partnerUniqueNumber}`}
                />
              </ListItem>
            ))}
          </List>
        )}

        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>Pair with</Typography>
          <TextField
            select fullWidth size="small" label="Device" value={partnerUno}
            onChange={(e) => setPartnerUno(e.target.value)}
            helperText={siblings.length ? '' : 'No other devices found at this location.'}
          >
            {siblings.map((d) => (
              <MenuItem key={d.UniqueNumber} value={String(d.UniqueNumber)}>
                {`${d.DeviceName || d.UniqueNumber}${d.RoomName ? ` · ${d.RoomName}` : ''}`}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            select fullWidth size="small" label="Button" value={partnerOutput}
            onChange={(e) => setPartnerOutput(e.target.value)}
            disabled={!partnerUno || partnerOutputs.length === 0}
            sx={{ mt: 1.5 }}
          >
            {partnerOutputs.map((o) => (
              <MenuItem key={o.Output} value={String(o.Output)}>
                {o.OutputName || `Button ${o.Output}`}
              </MenuItem>
            ))}
          </TextField>

          <Button
            variant="contained" onClick={add} sx={{ mt: 1.5 }}
            disabled={busy || !partnerUno || !partnerOutput}
          >
            Add pair
          </Button>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
